"use client"

import { useState, useEffect } from "react"
import "../styles/Admin.css"

// Styles object for the table
const styles = {
  th: {
    padding: '12px 15px',
    textAlign: 'left',
    fontWeight: '600',
    textTransform: 'uppercase',
    fontSize: '12px',
    letterSpacing: '0.5px'
  },
  td: {
    padding: '12px 15px',
    borderBottom: '1px solid #e0e0e0',
    verticalAlign: 'middle'
  },
  tr: {
    borderBottom: '1px solid #e0e0e0',
    ':hover': {
      backgroundColor: '#f8f9fa'
    }
  }
};

const Admin = () => {
  // Authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  
  // Admin data states
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("");
  
  // Discount code generation states
  const [batchSize, setBatchSize] = useState(1);
  const [fixedPrice, setFixedPrice] = useState(1000);
  const [usageLimit, setUsageLimit] = useState(1);
  const [batchName, setBatchName] = useState("");
  const [generatedCodes, setGeneratedCodes] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('adminGeneratedCodes')) || [];
    } catch {
      return [];
    }
  });
  const [generationError, setGenerationError] = useState("");
  
  // Admin password from environment variable
  const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD || 'admin@2024';

  // Fetch all existing discount codes on mount / after auth
  const fetchAllDiscountCodes = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5002';
      const adminToken = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      if (!adminToken) {
        console.error('No admin token found');
        return;
      }
      const res = await fetch(`${apiUrl}/api/discount-codes`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setGeneratedCodes(json.data);
        // persist in localStorage for offline viewing
        localStorage.setItem('adminGeneratedCodes', JSON.stringify(json.data));
      } else {
        console.error('Failed to fetch codes', json.message);
      }
    } catch(e){ console.error('Error fetching codes', e);}  };

  useEffect(() => { fetchAllDiscountCodes(); }, [isAuthenticated]);

  // Function to generate discount codes
  const generateDiscountCodes = async () => {
    try {
      setGenerationError("");
      setGeneratedCodes([]);
      
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5002';

      // Get the admin token from localStorage or session
      const adminToken = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      console.log('Admin token from storage:', adminToken ? 'Token exists' : 'No token found');
      
      if (!adminToken) {
        const errorMsg = "Authentication required. Please log in again.";
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      const requestBody = {
        count: parseInt(batchSize),
        fixedPrice: parseFloat(fixedPrice),
        currency: "USD",
        usageLimit: parseInt(usageLimit),
        batchName: batchName || `BATCH-${new Date().toISOString().slice(0, 10)}`,
        notes: "Generated by admin",
      };

      console.log('Sending request to:', `${apiUrl}/api/discount-codes/batch`);
      console.log('Request headers:', {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${adminToken}`
      });
      console.log('Request payload:', requestBody);

      const response = await fetch(`${apiUrl}/api/discount-codes/batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${adminToken}`
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status, response.statusText);
      const result = await response.json().catch(e => {
        console.error('Failed to parse JSON response:', e);
        throw new Error('Invalid response from server');
      });
      
      console.log('Server response:', result);
      
      if (!response.ok) {
        console.error('Error response:', result);
        throw new Error(result.message || `Failed to generate codes: ${response.statusText}`);
      }

      if (!result.success) {
        console.error('Operation not successful:', result);
        throw new Error(result.message || "Failed to generate codes: Server returned unsuccessful status");
      }

      // Debug: Log the exact structure of the response
      console.log('Response structure:', {
        hasData: !!result.data,
        dataType: Array.isArray(result.data) ? 'array' : typeof result.data,
        dataLength: Array.isArray(result.data) ? result.data.length : 'N/A',
        hasCodes: !!result.codes,
        codesType: Array.isArray(result.codes) ? 'array' : typeof result.codes,
        codesLength: Array.isArray(result.codes) ? result.codes.length : 'N/A'
      });

      // Handle case where data might be in different format
      const generatedData = Array.isArray(result.data) ? result.data : 
                           Array.isArray(result.codes) ? result.codes : [];
      
      if (generatedData.length === 0) {
        console.error('No codes generated. Response:', result);
        throw new Error("No codes were generated. The server returned an empty result.");
      }

      console.log('Generated codes:', generatedData);
      setGeneratedCodes(prev => {
        const merged = [...prev, ...generatedData];
        try {
          localStorage.setItem('adminGeneratedCodes', JSON.stringify(merged));
        } catch {}
        return merged;
      });
      
      // Show success message
      alert(`Successfully generated ${generatedData.length} discount codes!`);
      
      // Clear the form after successful generation
      setBatchSize(1);
      setFixedPrice(1000);
      setUsageLimit(1);
      // Don't clear batch name so user can generate multiple batches with the same name
      
    } catch (error) {
      console.error("Error generating discount codes:", {
        error: error.message,
        name: error.name,
        stack: error.stack
      });
      const errorMessage = error.response?.data?.message || error.message || "Failed to generate discount codes. Please try again.";
      setGenerationError(errorMessage);
      alert(`Error: ${errorMessage}`);
    }
  };

  // Debug logging to see current state
  console.log("Current state:", {
    loading,
    error,
    registrationsLength: registrations.length,
    selectedRegistration: !!selectedRegistration,
  })

  // Fetch all registrations with improved error handling
  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      setError(null); // Clear previous errors

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5002';
        
      const response = await fetch(`${apiUrl}/api/register`);

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.text();
          console.log("Error response:", errorData);
          // Try to parse as JSON if it looks like JSON
          if (errorData.trim().startsWith("{") || errorData.trim().startsWith("[")) {
            const parsedError = JSON.parse(errorData);
            errorMessage = parsedError.message || errorMessage;
          }
        } catch (parseError) {
          console.log("Could not parse error response as JSON:", parseError);
        }
        throw new Error(errorMessage);
      }

      // Get response as text first to debug
      const responseText = await response.text();
      console.log("Raw API Response:", responseText);

      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("JSON Parse Error:", parseError);
        console.error("Response that failed to parse:", responseText);
        throw new Error("Invalid JSON response from server");
      }

      console.log("Parsed API Response:", data);

      // Check if data is an array or if it's nested under a data property
      const registrationsData = Array.isArray(data) ? data : data.data || [];
      console.log("Processed registrations:", registrationsData);
      
      // Update the registrations state
      setRegistrations(registrationsData);
    } catch (err) {
      console.error("Error fetching registrations:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch registrations when component mounts and is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchRegistrations();
    }
  }, [isAuthenticated]);

  // Handle payment status update with improved error handling
  const handlePaymentStatusUpdate = async (id) => {
    if (!paymentStatus) return

    try {
      const apiUrl = import.meta.env.PROD ? "https://africa-maritime-talent.onrender.com" : "http://localhost:5002"
      const response = await fetch(`${apiUrl}/api/register/${id}/payment`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ paymentStatus }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error("Update error response:", errorText)
        throw new Error(`Failed to update payment status: ${response.status}`)
      }

      // Parse response safely
      const responseText = await response.text()
      let result
      try {
        result = JSON.parse(responseText)
      } catch (parseError) {
        console.log("Update response (not JSON):", responseText)
        result = { success: true } // Assume success if we can't parse but status was ok
      }

      // Update the registration in the state
      setRegistrations(registrations.map((reg) => (reg._id === id ? { ...reg, paymentStatus: paymentStatus } : reg)))

      // Close the detail view
      setSelectedRegistration(null)
      setPaymentStatus("")

      alert("Payment status updated successfully")
    } catch (err) {
      console.error("Error updating payment status:", err)
      setError(err.message)
    }
  }

  // Format date
  const formatDate = (dateString) => {
    try {
      const options = { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }
      return new Date(dateString).toLocaleDateString(undefined, options)
    } catch (error) {
      return "Invalid Date"
    }
  }

  // Handle view details
  const handleViewDetails = (registration) => {
    setSelectedRegistration(registration);
  }

  // Close modal
  const closeModal = () => {
    setSelectedRegistration(null);
  }

  // Format payment status for display
  const formatPaymentStatus = (status) => {
    if (!status) return 'Pending';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  // Get payment package name
  const getPackageName = (option) => {
    return option === "3day" ? "3-Day Full Access ($1,500)" : "2-Day Access ($1,000)"
  }

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "completed":
        return "status-badge status-completed"
      case "failed":
        return "status-badge status-failed"
      default:
        return "status-badge status-pending"
    }
  }

  // Debug styles
  const debugStyles = {
    border: '1px solid red',
    padding: '10px',
    margin: '10px 0'
  };



  // Handle password submission
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError("");
      localStorage.setItem('adminToken', 'dummy-token'); // Simple token for demo
      // Load registrations after successful authentication
      fetchRegistrations();
      fetchAllDiscountCodes();
    } else {
      setAuthError("Incorrect password. Please try again.");
    }
  };

  // Logout function
  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword("");
    setRegistrations([]);
  };

  // Password prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-gray-100 p-4" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Admin Access</h1>
            <p className="text-gray-600 mt-2">Please enter the admin password to continue</p>
          </div>
          
          {authError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {authError}
            </div>
          )}
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter admin password"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Sign In
            </button>
          </form>
        </div>
      </section>
    );
  }

  // Main admin interface
  return (
    <section className="bg-white " style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>

    <div className="admin-container">
      <div className="admin-header">
        <div className="flex justify-between items-center">
          <div>
            <h1>Registration Management</h1>
            <p>View and manage all registrations and payment statuses</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-sm"
          >
            Logout
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {loading && <div className="loading-spinner">Loading registrations...</div>}

      {!loading && !error && (
        <div className="admin-content">
          {selectedRegistration ? (
            // Registration details view (keep existing code)
            <div className="registration-details">
              <div className="details-header">
                <h2>Registration Details</h2>
                <button className="close-button" onClick={() => setSelectedRegistration(null)}>
                  ×
                </button>
              </div>

              <div className="details-content">
                <div className="detail-section">
                  <h3>Personal Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Name:</span>
                      <span className="detail-value">
                        {selectedRegistration.title} {selectedRegistration.firstName} {selectedRegistration.lastName}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{selectedRegistration.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Organization:</span>
                      <span className="detail-value">{selectedRegistration.organization || "N/A"}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{selectedRegistration.phoneNumber || "N/A"}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Address Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Address:</span>
                      <span className="detail-value">
                        {selectedRegistration.addressLine1}
                        {selectedRegistration.addressLine2 && <>, {selectedRegistration.addressLine2}</>}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">City:</span>
                      <span className="detail-value">{selectedRegistration.city}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">State/Province:</span>
                      <span className="detail-value">{selectedRegistration.state || "N/A"}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Country:</span>
                      <span className="detail-value">{selectedRegistration.country}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Postal Code:</span>
                      <span className="detail-value">{selectedRegistration.zip || "N/A"}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Registration Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Registration Date:</span>
                      <span className="detail-value">{formatDate(selectedRegistration.registrationDate)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Package:</span>
                      <span className="detail-value">{getPackageName(selectedRegistration.paymentOption)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Visa Required:</span>
                      <span className="detail-value">{selectedRegistration.requireVisa === "yes" ? "Yes" : "No"}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Current Payment Status:</span>
                      <div className="d-flex align-items-center">
                        <span className={getStatusBadgeClass(selectedRegistration.paymentStatus)}>
                          {formatPaymentStatus(selectedRegistration.paymentStatus)}
                        </span>
                        <button
                          className="btn btn-sm btn-link p-0 ms-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefreshStatus(selectedRegistration._id);
                          }}
                          disabled={loading}
                          title="Refresh status"
                        >
                          ⟳
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Update Payment Status</h3>
                  <div className="payment-update-form">
                    <div className="form-group">
                      <label htmlFor="paymentStatus">New Status:</label>
                      <select
                        id="paymentStatus"
                        value={paymentStatus}
                        onChange={(e) => setPaymentStatus(e.target.value)}
                      >
                        <option value="">Select status...</option>
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                    <button
                      className="update-button"
                      onClick={() => handlePaymentStatusUpdate(selectedRegistration._id)}
                      disabled={!paymentStatus}
                    >
                      Update Status
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Main table view
            <>
              <div className="registrations-summary">
                <div className="summary-card">
                  <h3>Total Registrations</h3>
                  <p className="summary-number">{registrations.length}</p>
                </div>
                <div className="summary-card">
                  <h3>Pending Payments</h3>
                  <p className="summary-number">
                    {registrations.filter((reg) => reg.paymentStatus === "pending").length}
                  </p>
                </div>
                <div className="summary-card">
                  <h3>Completed Payments</h3>
                  <p className="summary-number">
                    {registrations.filter((reg) => reg.paymentStatus === "completed").length}
                  </p>
                </div>
                <div className="summary-card">
                  <h3>Failed Payments</h3>
                  <p className="summary-number">
                    {registrations.filter((reg) => reg.paymentStatus === "failed").length}
                  </p>
                </div>
              </div>

              <div className="registrations-table-container" style={{
                border: '2px solid red',
                minHeight: '200px',
                position: 'relative'
              }}>
                <h2>All Registrations ({registrations.length})</h2>
                {registrations.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'rgba(255,0,0,0.1)',
                    padding: '5px',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    Table should be visible here
                  </div>
                )}


                <div style={{ margin: '20px 0' }}>
                  <h3 style={{
                    color: '#2c3e50',
                    borderBottom: '1px solid #e0e0e0',
                    paddingBottom: '10px',
                    marginBottom: '15px'
                  }}>
                    Database Registrations ({registrations.length})
                  </h3>
                  
                  {registrations.length === 0 ? (
                    <p style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: '#666',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      border: '1px dashed #ddd'
                    }}>
                      No registrations found in the database
                    </p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        <thead>
                          <tr style={{
                            backgroundColor: '#2c3e50',
                            color: 'white',
                            textAlign: 'left'
                          }}>
                            <th style={styles.th}>Name</th>
                            <th style={styles.th}>Email</th>
                            <th style={styles.th}>Package</th>
                            <th style={styles.th}>Payment Status</th>
                            <th style={styles.th}>Registration Date</th>
                            <th style={styles.th}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {registrations.map((registration) => (
                            <tr key={registration._id} style={{
                              borderBottom: '1px solid #e0e0e0',
                              ':hover': { backgroundColor: '#f8f9fa' }
                            }}>
                              <td style={styles.td}>
                                {registration.firstName} {registration.lastName}
                              </td>
                              <td style={styles.td}>
                                {registration.email}
                              </td>
                              <td style={styles.td}>
                                {getPackageName(registration.paymentOption) || 'N/A'}
                              </td>
                              <td style={styles.td}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  backgroundColor: (registration.paymentStatus === 'completed' || registration.paymentStatus === 'paid') ? '#d4edda' : '#fff3cd',
                                  color: (registration.paymentStatus === 'completed' || registration.paymentStatus === 'paid') ? '#155724' : '#856404',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  textTransform: 'capitalize'
                                }}>
                                  {registration.paymentStatus?.charAt(0).toUpperCase() + (registration.paymentStatus?.slice(1) || '') || 'Pending'}
                                </span>
                              </td>
                              <td style={styles.td}>
                                {new Date(registration.registrationDate).toLocaleDateString()}
                              </td>
                              <td style={styles.td}>
                                <button
                                  onClick={() => handleViewDetails(registration)}
                                  style={{
                                    padding: '6px 12px',
                                    marginRight: '8px',
                                    backgroundColor: '#3498db',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    ':hover': {
                                      backgroundColor: '#2980b9'
                                    }
                                  }}
                                >
                                  View Details
                                </button>
                                <button
                                  onClick={() => handleDeleteRegistration(registration._id)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#e74c3c',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    ':hover': {
                                      backgroundColor: '#c0392b'
                                    }
                                  }}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Registration Details Modal */}
      {selectedRegistration && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            <button 
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                '&:hover': {
                  color: '#000'
                }
              }}
            >
              &times;
            </button>
            
            <h2 style={{ marginTop: 0 }}>Registration Details</h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '16px',
              marginTop: '16px'
            }}>
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#2c3e50' }}>Personal Information</h3>
                <p><strong>Name:</strong> {selectedRegistration.title} {selectedRegistration.firstName} {selectedRegistration.lastName}</p>
                <p><strong>Email:</strong> {selectedRegistration.email}</p>
                <p><strong>Phone:</strong> {selectedRegistration.phoneNumber}</p>
                <p><strong>Organization:</strong> {selectedRegistration.organization}</p>
                <p><strong>Job Title:</strong> {selectedRegistration.jobTitle}</p>
              </div>
              
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#2c3e50' }}>Registration Details</h3>
                <p><strong>Package:</strong> {getPackageName(selectedRegistration.paymentOption)}</p>
                <p><strong>Registration Date:</strong> {formatDate(selectedRegistration.registrationDate)}</p>
                <p>
                  <strong>Payment Status:</strong> 
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    backgroundColor: (selectedRegistration.paymentStatus === 'completed' || selectedRegistration.paymentStatus === 'paid') ? '#d4edda' : '#fff3cd',
                    color: (selectedRegistration.paymentStatus === 'completed' || selectedRegistration.paymentStatus === 'paid') ? '#155724' : '#856404',
                    marginLeft: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'capitalize'
                  }}>
                    {selectedRegistration.paymentStatus?.charAt(0).toUpperCase() + (selectedRegistration.paymentStatus?.slice(1) || '') || 'Pending'}
                  </span>
                </p>
                {selectedRegistration.paymentReference && (
                  <p><strong>Payment Reference:</strong> {selectedRegistration.paymentReference}</p>
                )}
                {selectedRegistration.paymentError && (
                  <p><strong>Payment Error:</strong> {selectedRegistration.paymentError}</p>
                )}
              </div>
            </div>
            
            <div style={{ marginTop: '24px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#2c3e50' }}>Additional Information</h3>
              <p><strong>How did you hear about us?</strong> {selectedRegistration.hearAboutUs || 'N/A'}</p>
              {selectedRegistration.specialRequirements && (
                <p><strong>Special Requirements:</strong> {selectedRegistration.specialRequirements}</p>
              )}
            </div>
            
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={closeModal}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#2980b9'
                  }
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Discount Code Generator Section */}
    <div className="mt-8 p-6 bg-white rounded-lg shadow max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Generate Discount Codes</h2>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Number of Codes</label>
            <input
              type="number"
              min="1"
              value={batchSize}
              onChange={(e) => setBatchSize(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fixed Price (USD)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={fixedPrice}
              onChange={(e) => setFixedPrice(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Usage Limit</label>
            <input
              type="number"
              min="1"
              value={usageLimit}
              onChange={(e) => setUsageLimit(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Batch Name</label>
            <input
              type="text"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              placeholder="e.g., Summer2024"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={generateDiscountCodes}
            className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Generate Codes
          </button>
        </div>

        {generationError && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
            {generationError}
          </div>
        )}

        {generatedCodes.length > 0 && (
          <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Generated {generatedCodes.length} Discount Code{generatedCodes.length !== 1 ? 's' : ''}
                {batchName && ` (Batch: ${batchName})`}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price (USD)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usage Limit
                    </th>

                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {generatedCodes.map((code, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {code.code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {code.fixedPrice?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {code.usageLimit} time{code.usageLimit !== 1 ? 's' : ''}
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">1</span> to{' '}
                <span className="font-medium">{generatedCodes.length}</span> of{' '}
                <span className="font-medium">{generatedCodes.length}</span> codes
              </p>
              <button
                onClick={() => {
                  const codesText = generatedCodes.map(c => c.code).join('\n');
                  navigator.clipboard.writeText(codesText);
                  alert('Codes copied to clipboard!');
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Copy All Codes
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </section>
  )
}

export default Admin